import React from "react";
import {Typography} from "@mui/material";
import {useSessionContext} from "../../util/SessionContext";

// Right-aligned persona readout for a page's blue title bar: two white lines -- the persona type,
// then "Type: Name". Renders nothing when no persona is set.
//
// Factored out of the inline block in ProjectPlan.tsx so every Foundation page shows the same thing.
// That version dereferences getActiveVisibility()! with a non-null assertion; this one returns null
// instead, because a session with no active persona is a real state and should not crash the page.

// Per active-visibility Mode: the top-line `label`, the bottom-line `nameLabel` prefix (which entity
// the resolved name refers to), and the Account array that resolves it.
//
// Organization and Management are POSITION-family modes: the visibility Value stays the selected
// Position USID, so the top line shows the mode while the bottom line still reads "Position: <name>".
//
// BOTH spellings of the group modes are listed on purpose. The login form spells them without a space
// and the NavBar Personas menu spells them with one -- both end up in the session. Keying on only one
// spelling silently renders nothing for half of the personas, which looks like a missing feature
// rather than a lookup miss.
const PERSONA_MODES: Record<string, {label: string; nameLabel: string; accountKey: "Positions" | "UserGroups" | "WorkGroups"}> = {
    "Position": {label: "Position", nameLabel: "Position", accountKey: "Positions"},
    "Organization": {label: "Organization", nameLabel: "Position", accountKey: "Positions"},
    "Management": {label: "Manager", nameLabel: "Position", accountKey: "Positions"},
    "User Group": {label: "User Group", nameLabel: "User Group", accountKey: "UserGroups"},
    "UserGroup": {label: "User Group", nameLabel: "User Group", accountKey: "UserGroups"},
    "Work Group": {label: "Work Group", nameLabel: "Work Group", accountKey: "WorkGroups"},
    "WorkGroup": {label: "Work Group", nameLabel: "Work Group", accountKey: "WorkGroups"},
};

export function usePersona(): {type: string; nameLabel: string; name: string} | null {
    const sessionContext = useSessionContext();

    const visibility = sessionContext?.getActiveVisibility();
    if (!visibility || !visibility.Mode) {
        return null;
    }

    const mode = PERSONA_MODES[visibility.Mode];
    if (!mode) {
        return null;
    }

    const account: any = sessionContext?.getAccount();
    const match = (account?.[mode.accountKey] ?? []).find((entry: any) => entry.USID === visibility.Value);

    return {type: mode.label, nameLabel: mode.nameLabel, name: match?.Name ?? ""};
}

// When the type IS the name's label (Position, User Group, Work Group) the top line would only repeat the
// bottom one's prefix, so a single line "Position: <Name>" is shown instead.
export default function PersonaHeaderInfo() {
    const persona = usePersona();

    if (!persona) {
        return null;
    }

    return (
        <div style={{display: "flex", flexDirection: "column"}}>
            {persona.type !== persona.nameLabel &&
                <Typography style={{fontSize: 14, fontWeight: 400, color: "#FFFFFF"}}>{persona.type}</Typography>}
            <Typography style={{fontSize: 14, fontWeight: 400, color: "#FFFFFF"}}>{persona.nameLabel}: {persona.name}</Typography>
        </div>
    );
}
